import {
	IconChartActivityOutline18 as Activity,
	IconAtom2Outline18 as Atom,
	IconBookOpen4Outline18 as BookOpen,
	IconBugOutline18 as Bug,
	IconArrowRotateAnticlockwiseCheckOutline18 as ArrowRotateCheck,
	IconConnectedDots2Outline18 as ConnectedDots,
	IconDatabaseOutline18 as Database,
	IconMessageSparkleOutline18 as MessageSparkle,
	IconBoltLightningOutline18 as BoltLightning,
	IconTelescopeOutline18 as Telescope,
} from "nucleo-ui-outline-18"

import { connectorReachesAgent } from "@productlane/lib/src/browser"
import { AiAssistantIcon } from "@productlane/ui/src/icons/project/ai-assistant-icon"
import { CursorIcon } from "@productlane/ui/src/icons/project/cursor-icon"
import { GitHubIcon } from "@productlane/ui/src/icons/project/github-icon"

import type { AiSettingsRowIcon as IconType } from "./ai-settings-row"
import { MetaInfo } from "@/components/meta-info"
import { api } from "@/lib/api"
import { useZeroWorkspace } from "@/lib/zero"
import { SettingsHeader } from "../settings-header"
import {
	aiSettingsGroupClass as containerClass,
	AiSettingsRow as Row,
} from "./ai-settings-row"

export function AiOverviewPage() {
	const workspace = useZeroWorkspace()
	const { data: pricing } = api.workspace.aiResolutionPricing.useQuery()
	const { data: tokenUsage } = api.workspace.getAiTokenUsage.useQuery({})
	const { data: info } = api.workspace.info.useQuery(undefined, {
		staleTime: 60_000,
	})

	if (!workspace) return null

	const usedResolutions = pricing?.usedThisMonth ?? 0
	const includedResolutions = pricing?.freeResolutions ?? 0

	const creditsSpent = tokenUsage?.spentThisMonthUsd ?? 0
	const creditsAvailable = tokenUsage
		? tokenUsage.capUsd === null
			? null
			: tokenUsage.freeAllowanceUsd + tokenUsage.capUsd
		: null
	const creditsText =
		creditsAvailable === null
			? `$${creditsSpent.toFixed(2)} credits`
			: `$${creditsSpent.toFixed(2)}/$${creditsAvailable.toFixed(2)} credits`

	const settings = workspace.aiChatSettings
	const cursorConnected = !!info?.cursorConnected

	const agentExposedSections = (workspace.sidebarSections ?? []).filter(
		(section) => !section.isDeleted && connectorReachesAgent(section),
	).length

	const features: {
		icon: IconType
		title: string
		description: string
		status: boolean
		link: string
	}[] = [
		{
			icon: Atom,
			title: "Agent",
			description: "Resolve conversations in your widget, live chat and docs.",
			status: settings?.isEnabled ?? false,
			link: "/settings/ai/agent",
		},
		{
			icon: AiAssistantIcon,
			title: "AI Assistant",
			description:
				"Ask the in-thread assistant to research and answer for you.",
			status: true,
			link: "/settings/ai/assistant",
		},
		{
			icon: ArrowRotateCheck,
			title: "Close loop",
			description:
				"Tell customers when the Linear issue or project they asked for ships.",
			status: settings?.closeLoopAutoSend ?? false,
			link: "/settings/ai/close-loop",
		},
		{
			icon: BoltLightning,
			title: "Routines",
			description:
				"Tag, prioritize, and route every thread as its messages arrive.",
			status: settings?.routinesEnabled ?? false,
			link: "/settings/ai/routines",
		},
		{
			icon: MessageSparkle,
			title: "AI Drafts",
			description:
				"Populate the composer with a draft reply in the background.",
			status: settings?.aiDraftsEnabled ?? false,
			link: "/settings/ai/drafts",
		},
		{
			icon: Bug,
			title: "Bug Inspect",
			description: "Check bug reports against your code and draft a fix.",
			status:
				(settings?.bugInspectEnabled ?? false) &&
				!!workspace.githubSettings?.repositoryFullName,
			link: "/settings/ai/bug-inspect",
		},
		{
			icon: BookOpen,
			title: "Self-updating docs",
			description:
				"Draft help center articles from released issues and commits.",
			status: workspace.autoProposeDocsUpdates,
			link: "/settings/docs",
		},
		{
			icon: Telescope,
			title: "Release Intelligence",
			description:
				"Generate changelog drafts from Linear issues and GitHub commits.",
			status: workspace.changelogAutomaticDrafts ?? false,
			link: "/settings/changelog",
		},
	]

	return (
		<>
			<MetaInfo title="AI Settings" />

			<SettingsHeader
				title="AI"
				subtitle="Configure Productlane's AI across your workspace."
				docsArticle="c8cfc4d9-dec0-4188-b31c-f55ffd758d58"
			/>

			<div className={`${containerClass} mb-8`}>
				<Row
					icon={Activity}
					title="AI usage and credits"
					description={`${usedResolutions}/${includedResolutions} resolutions · ${creditsText}`}
					link="/settings/ai/usage"
				/>
			</div>

			<div className={containerClass}>
				{features.map((feature) => (
					<Row key={feature.title} {...feature} />
				))}
			</div>

			<div className="mb-3 mt-8 flex items-center gap-2">
				<span className="text-sm font-medium text-primary">Integrations</span>
			</div>

			<div className={containerClass}>
				<Row
					icon={GitHubIcon}
					title="GitHub"
					description="Ground answers and draft docs from your connected repositories."
					link="/settings/integrations/github"
				/>
				<Row
					icon={Database}
					title="PostgreSQL"
					description="Let the AI Assistant run read-only queries against your database."
					link="/settings/integrations/postgres"
				/>
				{cursorConnected && (
					<Row
						icon={CursorIcon}
						title="Cursor"
						description="Auto-assign a coding agent to resolve bug reports with Cursor Cloud."
						link="/settings/integrations/cursor"
					/>
				)}
				<Row
					icon={ConnectedDots}
					title="Connectors"
					description="Look up live data from your own API or an MCP server."
					link="/settings/custom-connectors"
					status={agentExposedSections > 0}
				/>
			</div>
		</>
	)
}
